import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Accordion, AccordionDetails, AccordionSummary, Button, Icon, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { GridExpandMoreIcon } from '@mui/x-data-grid';

import ProgressBar from '../controls/ProgressBar';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';

import { userManagementService } from '../../services/user-management.service';
import { plannenService } from '../../services/plannen.service';
import { eventBus } from '../../services/event-emitter.service';
import { mapService } from '../../services/map.service';
import { planManagementService } from '../../services/plan-management.service';
import { Beoordeling } from '../../models/plan/beoordeling';
import { InfraPlan } from '../../models/plan/plan';

import PlanBeoordelingen from './PlanBeoordelingen';
import DialogStartSharingOrganisatie from '../controls/dialogs/DialogStartSharingOrganisatie';
import SharedOrganisatiesByPlan from '../authorization/organisaties/SharedOrganisatiesByPlan';
import { ListParams } from '../../models/listParams';
import DialogBeoordelingAdd from '../controls/dialogs/DialogBeoordelingAdd';
import { IdTokenClaims } from '@azure/msal-browser';
import { TokenClaims } from '@azure/msal-common/lib/types/exports-common';
import { jwtDecode } from 'jwt-decode';
import DialogPortfolioStart from '../controls/dialogs/DialogPortfolioStart';
import OverzichtPortfolios from '../portfolios/Overzicht';
import { portfolioService } from '../../services/portfolio.service';
import { Portfolio } from '../../models/portfolio/portfolio';
import { helperService } from '../../services/helper.service';
import { authService } from '../../services/auth.service';
import ErrorMessage from '../controls/ErrorMessage';


interface PlanDetailProps {
  planId?: string; // ID of the current plan
  enableZoomInOnPlanBbox?: boolean;
  onClosePlan?: () => void;
  refetchPlannen?: () => void;
  setPlanNaam?: (title: string) => void;
  setPlanOmschrijving?: (title: string) => void;
}

const PlanDetail: React.FC<PlanDetailProps> = ({ 
  planId,
  enableZoomInOnPlanBbox,
  onClosePlan,
  refetchPlannen,
  setPlanNaam,
  setPlanOmschrijving
}) => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const account = accounts[0];

  const { id: urlPlanId } = useParams<{ id: string }>(); // Get ID from URL
  const canCrud = authService.canCrud();
  const isKeyUser = authService.isKeyUser();

  // State to store the list of plans
  const [plan, setPlan] = useState<any>(null);
  const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);
  const [roles, setRoles] = useState<string[] | undefined>([]);
  const [decodedToken, setDecodedToken] = useState<IdTokenClaims | null>();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  
  const hasMijnGegevensData = useRef(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [organisatiesShared, setOrganisatiesShared] = useState<any[] | null>(null);

  // const [callbackKey, setCallbackKey] = useState(0); // Used to force re-fetching in OrganisatieUsers
  const [callbackKeys, setCallbackKeys] = useState({
    portfolio: 0,
    beoordeling: 0,
    sharing: 0,
  });

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    beoordeling: false,
    sharing: false,
    snackbar: false,
    portfolio: false
  });

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const refreshCallbackKey = (keyName: keyof typeof callbackKeys) => {
    setCallbackKeys(prev => ({
      ...prev,
      [keyName]: prev[keyName] + 1
    }));
  };

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  const zichtbaarheidLabels: any = {
    private: {
      label: 'Privé',
      description: 'Plan wordt niet gedeeld met organisaties waarmee <naam-org> plannen deelt. Dit plan kan handmatig met andere organisaties worden gedeeld'
    },
    shared: {
      label: 'Gedeeld',
      description: 'Plan wordt gedeeld met organisaties waarmee <naam-org> plannen deelt.'
    }
  };

  let infplId = planId || urlPlanId; // Use prop ID if available, otherwise URL ID

  const fetchPlan = async () => {
    try {
      if (infplId) {
        setLoading(true);
        const data = await plannenService.getPlan(infplId) as InfraPlan;

        if (data) {
          const result = await planManagementService.getPlanBeoordelingenCurrent(infplId) as any;
          if (result && result.planBeoordelingen) {
            const beoordelingen = result.planBeoordelingen as Beoordeling[];
            data.beoordelingen = beoordelingen || [];

            // Tel alle remarks die wél beoordeeld zijn
            const beoordeeldCount = data.beoordelingen.filter(x => x.key?.id !== "nog_niet_beoordeeld").length;

            // Totale aantal remarks
            const totalCount = result.totalCount;

            // extra params opslaan in state
            setPlan({ 
              ...data, 
              beoordeeldCount, 
              totalCount
            });

          }
          else {
            setPlan(data);
          }

          if (setPlanNaam && data.planNaam) {
            setPlanNaam(data.planNaam);
          }

          if (setPlanOmschrijving && data.planOmschrijving) {
            setPlanOmschrijving(data.planOmschrijving);
          }

          if (mijnOrganisatie && plan?.infplOrganizationId === mijnOrganisatie?.id) {
            getSharedOrganisationByPlan();
          }

          if (enableZoomInOnPlanBbox) {
            eventBus.emit('zoomInOnPlanBbox', data.bbox);
          }

          // Emit selectPlan event to highlight this plan on the map
          if (data.infplId) {
            eventBus.emit('selectPlan', data.infplId);
          }
        }
        // // const bbox = [4.34, 52.06, 4.36, 52.08]; // Default for now
        // const bbox = [4.155406951903785, 51.90509556134839, 4.762744903563885, 51.939397040593406];
        // // 78037.91172574727%2C456087.19672466273%2C108224.0573643787%2C469916.1788749598

        // const [west, south] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]);
        // const [east, north] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]);

        // const wfsParams = new WfsParams();
        // wfsParams.offset = 0;
        // wfsParams.limit = 1000;
        // // wfsParams.bbox = `${bbox[0]},${bbox[1]},${bbox[2]},${bbox[3]}`; // '4.34,52.06,4.36,52.08'; // Default for now
        // wfsParams.bbox = `${west},${south},${east},${north}`; // 78037.91172574727%2C456087.19672466273%2C108224.0573643787%2C469916.1788749598

        // const data = await planService.getPlannenWFS(wfsParams);
        // // console.log('wfs data', data);

        // setPolygons(data); // Assuming the API response is a JSON array of plans
        setLoading(false);
      }
    }
    catch (error: any) {
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const fetchMijnGegevens = async () => {
    setLoading(true);
    try {
      // Mijn gegevens
      //console.log('accounts', accounts);
      //console.log('instance', instance);

      const idToken = account?.idToken; // account?.idTokenClaims?.idToken;

      // Decode the token to read claims
      let roles: string[] | undefined;
      if (idToken) {
        const decodedToken = jwtDecode<TokenClaims>(idToken as string); // Cast idToken to string
        //console.log('decodedToken', decodedToken);
        setDecodedToken(decodedToken);
        roles = decodedToken.roles;

        // console.log(roles);
        setRoles(roles);
      }

      const storedOrganisatie = userManagementService.getStoredOrganisatie();
      if (storedOrganisatie) {
        setMijnOrganisatie(storedOrganisatie);
      }
      
      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  // Fetch the plans data from API when the component mounts
  useEffect(() => {
    if (!infplId) return;

    if (!hasFetchedData.current) {
      fetchPlan();
      // hasFetchedData.current = true; // Mark as fetched
    } 
  }, [infplId]); // Run whenever infplId changes // [] means only once

  useEffect(() => {

    if (!hasMijnGegevensData.current) {
      fetchMijnGegevens();
      hasMijnGegevensData.current = true; // Mark as fetched
    }
  }, []);

  useEffect(() => {
    const handleSelectPlan = (planId: string | null) => {
      setSelectedPlanId(planId);
    };
    
    eventBus.on('selectPlan', handleSelectPlan);
    
    return () => {
      eventBus.off('selectPlan', handleSelectPlan);
    };
  }, []);


  const onClickClosePlan = async () => {
    infplId = undefined;
    setPlan(null);
    if (onClosePlan) {
      onClosePlan();
    }
  };

  const getBboxLatLng = (bbox: number[]) => {
    const bboxLatLng = mapService.convertBboxRDToLatLng(bbox, true);
    return bboxLatLng;
  };

  const getBeoordelingen = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (!isExpanded) return;
  
    // console.log("Fetching beoordelingen...");
    // await fetchBeoordelingen(); // eventueel async maken
  };

  const onClickSharePlan = async () => {
    openDialog('sharing');
  };

  const onClickStartPortfolio = async () => {
    openDialog('portfolio');
  }

  const getSharedOrganisationByPlan = async () => {
    if (infplId) {
      const params: ListParams = {
        offset: 0, 
        limit: 100
      };

      const result = await planManagementService.getOrganisationsSharedWithPlan(infplId, params) as any;
      setOrganisatiesShared(result.items);
    }
  };

  // Function to trigger a refresh by changing the key
  const refetchSharedOrganisatiesByPlan = () => {
    getSharedOrganisationByPlan();
    refreshCallbackKey('sharing'); // Increment the key to force re-render
  };

  const handleConfirmPlanSharing = () => {
    closeDialog('sharing');

    setSnackbarMessage('Plan gedeeld met organisatie');
    openDialog('snackbar');

    refetchSharedOrganisatiesByPlan();
  };

  const onClickBeoordeelPlan = async () => {
    openDialog('beoordeling');
  };

  const handleSubmitBeoordeling = async (beoordeling: any) => {
    try {
      setLoading(true);
      if (infplId) {
        const response = await planManagementService.addPlanBeoordeling(beoordeling, infplId);
        if (response.ok) {
          closeDialog('beoordeling');

          if (onSubmitBeoordeling) {
            onSubmitBeoordeling();
          }
        }
      } 
      else {
        console.error('Geen planId gekozen om beoordeling aan toe te voegen');
      }
      setLoading(false);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating beoordeling:', error);
      setError('Er is een fout opgetreden');
    }
  };

  const onSubmitBeoordeling = async () => {
    // Na het toevoegen van een beoordeling, de lijst met beoordelingen opnieuw ophalen
    if (infplId) {
      await fetchPlan();
      refetchPlannen && refetchPlannen();
    }
  }

  const handleSubmitPortfolio = async (portfolio: Portfolio) => {
    try {
      setLoading(true);
      console.log(portfolio);
      
      if (infplId) {
        portfolio.infplId = infplId;
        const response = await portfolioService.createPortfolio(portfolio);
        
        if (response.ok) {
          closeDialog('portfolio');
          setSnackbarMessage('Afstemmingsdossier is succesvol aangemaakt');
          openDialog('snackbar');

          refreshCallbackKey('portfolio'); // Increment the key to force re-render
        }
      } 
      else {
        console.error('Geen planId gekozen om portfolio aan toe te voegen');
      }
      setLoading(false);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating portfolio:', error);
      setError('Er is een fout opgetreden');
    }
  };

  const onSubmitPortfolio = async () => {
    // Na het toevoegen van een portfolio, de lijst met portfolios opnieuw ophalen
    if (infplId) {
      // refetch
    }
  }

  if (error) {
    return (
      <>
        <div className="text-left">
            <IconButton className='btn-detail-close' size="large" onClick={() => onClickClosePlan()}>
              <Icon>clear</Icon>
            </IconButton>

            <ErrorMessage message={error} />;
        </div>
      </>
    )
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      {isAuthenticated && (
        <div className="text-left">
          {plan && mijnOrganisatie && (
            <div className="navigation nav-functions">
              {plan?.infplOrganizationId === mijnOrganisatie?.id && canCrud && (
                <Tooltip title="Start een nieuw afstemmingsdossier met dit plan" arrow>
                  <IconButton className='btn-icon btn-start-portfolio' color="secondary" size="small" onClick={() => onClickStartPortfolio()}>
                    <Icon>work</Icon>
                  </IconButton>
                </Tooltip>
              )}

              {plan?.infplOrganizationId === mijnOrganisatie?.id && isKeyUser && (
                <Tooltip title="Plan delen met organisaties" arrow>
                  <IconButton className='btn-icon btn-deel-plan' color="secondary" size="small" onClick={() => onClickSharePlan()}>
                    <Icon>share</Icon>
                  </IconButton>
                </Tooltip>
              )}

              {plan?.infplOrganizationId !== mijnOrganisatie?.id && canCrud && (
                <Tooltip title="Beoordeling wijzigen" arrow>
                  <IconButton className='btn-icon btn-beoordeel-plan' color="secondary" size="small" onClick={() => onClickBeoordeelPlan()}>
                    <Icon>star</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}

          { planId && onClosePlan && (
            <Tooltip title="Sluiten" arrow>
              <IconButton className='btn-icon btn-detail-close' size="large" onClick={() => onClickClosePlan()}>
                <Icon>clear</Icon>
              </IconButton>
            </Tooltip>
          )}

          { mijnOrganisatie ? (
            <>
              {/* <pre>
                <code>{JSON.stringify(plan, null, 2)}</code>
              </pre> */}

              {infplId && plan && (
                <div className={planId ? "scrollable-area" : ""}>

                  <Accordion className="transparent-accordion" defaultExpanded>
                    <AccordionSummary
                      expandIcon={<GridExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Tooltip title={plan.discipline} arrow>
                        <span className={`category-icon category-icon-small ${helperService.toCamelCase(plan.discipline)}-icon-white`}></span>
                      </Tooltip>
                      <Typography component="span">
                        <span>{plan.planNaam || '-'}</span>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      
                      <div className="container">
                        <div className="row">
                          <div className="col-xxl-12 col-xl-12 col-lg-12">
                            <label>Omschrijving</label>
                            <p>{plan.planOmschrijving || '-'}</p>
                          </div>

                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Plan ID</label>
                            <p>{plan.planId}</p>
                          </div>

                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Status</label>
                            <p>{plan.planStatus}</p>
                          </div>

                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Zichtbaarheid</label>
                            <p>
                              {zichtbaarheidLabels[plan.deelNiveau.toLowerCase()]?.label || plan.deelNiveau}
                              <Tooltip
                                title={zichtbaarheidLabels[plan.deelNiveau.toLowerCase()]?.description?.replace(/<naam-org>/g, plan.infplOrganizationName || '') || ''}
                                placement="right"
                                arrow
                              >
                                <Icon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5 }}>
                                  info
                                </Icon>
                              </Tooltip>
                            </p>

                          </div>

                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Startdatum</label>
                            {plan.planStartDatum ? (
                              <p>
                                {plan.planStartDatum.startsWith('0001')
                                  ? '-'
                                  : new Date(plan.planStartDatum).toLocaleDateString('nl-NL')}
                              </p>
                            ) : (
                              <p>-</p>
                            )}
                          </div>
                          
                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Einddatum</label>
                            {plan.planEindDatum ? (
                              <p>
                                {plan.planEindDatum.startsWith('0001')
                                  ? '-'
                                  : new Date(plan.planEindDatum).toLocaleDateString('nl-NL')}
                              </p>
                            ) : (
                              <p>-</p>
                            )}
                          </div>

                        </div>
                        <div className="row">
                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Type</label>
                            <p>{plan.planType}</p>
                          </div>
                          {/* <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Discipline</label>
                            <p>
                              <span className={`category-icon category-icon-small ${helperService.toCamelCase(plan.discipline)}-icon-white`}>
                                <span>{plan.discipline}</span>
                              </span>
                            </p>
                          </div> */}
                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            <label>Organisatie</label>
                            <p>{plan.infplOrganizationName || 'Geen organisatie'}</p>
                            {/* <span>({plan.infplOrganizationId || '-'})</span> */}
                          </div>


                          {plan.kosten != null && (
                            <div className="col-xxl-4 col-xl-4 col-lg-6">
                              <label>Kosten</label>
                              <p>{new Intl.NumberFormat('nl-NL', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    minimumFractionDigits: 2,
                                }).format(plan.kosten)}
                              </p>
                            </div>
                          )}

                          {plan.labels.length === 0 && (
                            <div className="row" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', paddingBottom: '10px', marginBottom: '15px' }}></div>
                          )}
                        </div>

                        {plan.labels.length > 0 && (
                          <div className="row" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', paddingBottom: '10px', marginBottom: '15px' }}>
                            <div className="col-xxl-12 col-xl-12 col-lg-12">
                              <label>Labels</label>
                            </div>
                            <div className="col-xxl-12 col-xl-12 col-lg-12">
                              <p>{ plan.labels.join(', ') }</p>
                            </div>
                          </div>
                        )}

                        {plan.extraVelden.length > 0 && (
                          <div className="row" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', paddingBottom: '10px', marginBottom: '15px' }}>
                            <div className="col-xxl-12 col-xl-12 col-lg-12">
                              <h6>Extra informatie</h6>
                            </div>
                            {plan.extraVelden.map((keyValuePair: any, index: number) => (
                              <div className="col-xxl-4 col-xl-4 col-lg-6">
                                <label>{ keyValuePair.key }</label>
                                <p>{ keyValuePair.value }</p>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="row row-plan-detail-meta">
                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            {plan.aanmaakDatum && (
                              <div className="date date-created">
                                <Tooltip title={`Aangemaakt op ${new Date(plan.aanmaakDatum).toLocaleDateString('nl-NL')}`} arrow>
                                  <span className="tooltip-content">
                                    <Icon>event_incoming</Icon>
                                    <span>{new Date(plan.aanmaakDatum).toLocaleDateString('nl-NL')}</span>
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            {plan.infplLaatstGewijzigd && (
                              <div className="date date-last-change">
                                <Tooltip title={`Laatst gewijzigd op ${new Date(plan.infplLaatstGewijzigd).toLocaleDateString('nl-NL')}`} arrow>
                                  <span className="tooltip-content">
                                    <Icon>edit_calendar</Icon>
                                    {/* <strong>Laatst gewijzigd op: </strong> */}
                                    <span>{new Date(plan.infplLaatstGewijzigd).toLocaleDateString('nl-NL')}</span>
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                          <div className="col-xxl-4 col-xl-4 col-lg-6">
                            {plan.infplLaatstGesynced && (
                              <div className="date date-last-sync">
                                <Tooltip title={`Laatst gesynct op ${new Date(plan.infplLaatstGesynced).toLocaleDateString('nl-NL')}`} arrow>
                                  <span className="tooltip-content">
                                    <Icon>event_repeat</Icon>
                                    {/* <strong>Laatst gesynct op: </strong> */}
                                    <span>{new Date(plan.infplLaatstGesynced).toLocaleDateString('nl-NL')}</span>
                                  </span>
                                </Tooltip>
                              </div>
                            )}
                          </div>

                          {/* <div className="plan-detail-meta">
                          </div> */}
                          
                        </div>
                      </div>

                    </AccordionDetails>
                  </Accordion>

                  {plan.contactPersonen.length > 0 && (
                    <Accordion className="transparent-accordion">
                      <AccordionSummary
                        expandIcon={<GridExpandMoreIcon />}
                        aria-controls="panel-contacts-content"
                        id="panel-contacts-header"
                      >
                        <Icon>contacts</Icon>
                        <Typography component="span">Contactpersonen</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                      
                        <div className="contact-grid">
                          {plan.contactPersonen.length > 0 ? (
                            plan.contactPersonen.map((contact: any, index: number) => (
                              <div className="contact-card" key={index}>
                                <div className="contact-header">
                                  <h3>{contact.naam?.trim() || 'Onbekend'}</h3>
                                  {contact.functie && <span>{contact.functie}</span>}
                                </div>

                                <div className="contact-details-wrapper">
                                  {contact.telefoon ? (
                                    <div className="contact-detail">
                                      <span className="material-icons">phone</span>
                                      {contact.telefoon}
                                    </div>
                                  ) : (
                                    <div className="contact-detail" />
                                  )}

                                  {contact.email ? (
                                    <div className="contact-detail">
                                      <span className="material-icons">email</span>
                                      <a href={`mailto:${contact.email}`}>{contact.email.toLowerCase()}</a>
                                    </div>
                                  ) : (
                                    <div className="contact-detail" />
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <p>Geen contactpersonen</p>
                          )}
                      </div>

                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Accordion 
                    className="transparent-accordion">
                    <AccordionSummary
                      expandIcon={<GridExpandMoreIcon />}
                      aria-controls="panel-portfolio-content"
                      id="panel-portfolio-header"
                    >
                      <Icon>work</Icon>
                      <Typography component="span" sx={{ width: '33%', flexShrink: 0 }}>Afstemmingsdossiers</Typography>

                    </AccordionSummary>
                    <AccordionDetails>
                      
                      {/* <pre>{JSON.stringify(organisatiesShared, null, 2)}</pre> */}

                      {plan?.infplOrganizationId === mijnOrganisatie?.id && canCrud && (
                        <Tooltip title="Start een nieuw afstemmingsdossier met dit plan" arrow>
                          <Button variant="text" color="secondary" onClick={ () => onClickStartPortfolio() }>
                            <Icon>add</Icon>
                            Nieuw afstemmingsdossier
                          </Button>
                        </Tooltip>
                      )}
                      
                      <OverzichtPortfolios planId={plan.infplId} refreshKey={callbackKeys.portfolio} /> 
                      
                    </AccordionDetails>
                  </Accordion>

                  <Accordion 
                    className="transparent-accordion"
                    onChange={(event, isExpanded) => getBeoordelingen(event, isExpanded) }>
                    <AccordionSummary
                      expandIcon={<GridExpandMoreIcon />}
                      aria-controls="panel-beoordelingen-content"
                      id="panel-beoordelingen-header"
                    >
                      <Icon>star</Icon>
                      <Typography component="span" sx={{ width: '33%', flexShrink: 0 }}>Beoordelingen</Typography>
                      
                      <Typography component="span" className="summary" sx={{ color: 'text.secondary' }}>
                        {plan.beoordeeldCount} van de {plan.totalCount} beoordeeld
                      </Typography>

                    </AccordionSummary>
                    <AccordionDetails>
                      
                      <PlanBeoordelingen 
                        infplId={plan.infplId} 
                        planNaam={plan.planNaam ? plan.planNaam : undefined} 
                        beoordelingen={plan.beoordelingen} 
                        onSubmitBeoordeling={onSubmitBeoordeling}
                      />
                      
                    </AccordionDetails>
                  </Accordion>                 

                  {plan && mijnOrganisatie && plan?.infplOrganizationId === mijnOrganisatie?.id && (
                    <Accordion 
                      className="transparent-accordion">
                      <AccordionSummary
                        expandIcon={<GridExpandMoreIcon />}
                        aria-controls="panel-sharing-content"
                        id="panel-sharing-header"
                      >
                        <Icon>share</Icon>
                        <Typography component="span" sx={{ width: '33%', flexShrink: 0 }}>Plan gedeeld met</Typography>
                        
                        {organisatiesShared && (
                          <Typography component="span" className="summary" sx={{ color: 'text.secondary' }}>
                            {organisatiesShared.length} organisatie(s)
                          </Typography>
                        )}

                      </AccordionSummary>
                      <AccordionDetails>
                        
                        {/* <pre>{JSON.stringify(organisatiesShared, null, 2)}</pre> */}
                        {plan?.infplOrganizationId === mijnOrganisatie?.id && isKeyUser && (
                          <Tooltip title="Plan delen met een organisatie" arrow>
                            <Button variant="text" color="secondary" onClick={ () => onClickSharePlan() }>
                              <Icon>add</Icon>
                              Organisatie
                            </Button>
                          </Tooltip>
                        )}
                        
                        <SharedOrganisatiesByPlan planId={plan.infplId} key={callbackKeys.sharing} /> 
                        
                      </AccordionDetails>
                    </Accordion>
                  )}

                </div>
              )}
            </>
          ) : (
            <NoActiveOrganisatieMessage />
          )}

        </div>
      )}

      <DialogStartSharingOrganisatie
        open={open.sharing}
        title="Plan delen met organisatie"
        contentText="Kies een van de beschikbare organisaties waarmee u uw plan wilt delen en druk op Start met delen."
        sharing="plan"
        groupBySource
        planId={infplId}
        onClose={() => closeDialog('sharing')}
        onSubmit={handleConfirmPlanSharing}/>

      {canCrud && (
        <DialogBeoordelingAdd
          open={open.beoordeling}
          planNaam={plan ? plan.planNaam : 'Onbekend'}
          onClose={ () => closeDialog('beoordeling') }
          onSubmit={handleSubmitBeoordeling}/>
      )}

      <DialogPortfolioStart
        open={open.portfolio}
        plan={plan}
        onClose={ () => closeDialog('portfolio') }
        onSubmit={handleSubmitPortfolio} />

      { loading && (
        <ProgressBar invert={false} />
      )}
    </>
  );
};

export default PlanDetail;